// Theme & Style (System) — admin uploads/edits/removes the portal logo and
// favicon. Changes reflect portal-wide: the sidebar BrandLogo re-reads the theme
// config, and App re-applies the favicon on the 'stratus-theme-changed' event.

import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Palette, Upload, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';
import { StratusLogo } from '../components/StratusLogo';

export default function ThemeStyle() {
  const qc = useQueryClient();
  const cfg = useQuery<any>({ queryKey: ['theme-config'], queryFn: async () => { const r = await fetch('/api/theme/public/config'); return r.ok ? r.json() : {}; } });
  const [busy, setBusy] = useState('');
  const c = cfg.data || {};

  function announce() {
    qc.invalidateQueries({ queryKey: ['theme-config'] });
    window.dispatchEvent(new Event('stratus-theme-changed'));
  }
  async function upload(slot: 'logo' | 'favicon', file: File) {
    setBusy(slot);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await fetch(`/api/theme/${slot}`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error(await r.text());
      announce();
    } catch (e: any) { alert('Upload failed: ' + e.message); } finally { setBusy(''); }
  }
  async function reset(slot: 'logo' | 'favicon') {
    if (!confirm(`Reset ${slot} to the Stratus default?`)) return;
    setBusy(slot);
    try { await fetch(`/api/theme/${slot}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } }); announce(); }
    catch (e: any) { alert('Failed: ' + e.message); } finally { setBusy(''); }
  }
  async function setTheme(theme: string) {
    setBusy('theme');
    // Apply instantly for feedback, then persist portal-wide.
    const el = document.documentElement;
    if (theme && theme !== 'default') el.setAttribute('data-stratus-theme', theme); else el.removeAttribute('data-stratus-theme');
    try {
      const r = await fetch('/api/theme/theme', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ theme }) });
      if (!r.ok) throw new Error(await r.text());
      announce();
    } catch (e: any) { alert('Failed: ' + e.message); } finally { setBusy(''); }
  }
  const currentTheme = c.theme || 'beacon';  // Beacon is the default (matches App.tsx + server) — highlight it when unset

  return (
    <div className="space-y-4 max-w-3xl">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><Palette /> Theme &amp; Style</h1>
      <p className="text-textSecondary text-sm">
        Customize portal branding. Changes apply across the whole portal — theme, sidebar logo and the browser favicon — for every user.
      </p>

      <div className="card p-4 space-y-3">
        <h2 className="font-semibold">Portal theme</h2>
        <p className="text-textSecondary text-sm">Applies portal-wide for everyone. Pick <strong>Default</strong> any time to revert.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {THEME_OPTIONS.map((t) => (
            <ThemeTile key={t.id} t={t} active={currentTheme === t.id} busy={busy === 'theme'} onPick={() => setTheme(t.id)} />
          ))}
        </div>
      </div>

      <AssetCard
        title="Portal logo"
        desc="Shown in the sidebar. PNG, SVG, JPG or WebP. Wide marks work best — it scales to the sidebar height."
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        custom={!!c.logo} busy={busy === 'logo'}
        preview={c.logo
          ? <img src={`/api/theme/public/logo?v=${c.logo_v || 0}`} alt="logo" style={{ height: 40, maxWidth: 220, objectFit: 'contain' }} />
          : <span className="flex items-center gap-2"><StratusLogo size={32} withText={false} className="text-primary" /><span className="font-semibold">Stratus</span><em className="text-textSecondary text-xs">default</em></span>}
        onUpload={(f) => upload('logo', f)} onReset={() => reset('logo')}
      />

      <AssetCard
        title="Favicon"
        desc="The browser tab icon. SVG (recommended), PNG, or ICO. Square images look best."
        accept="image/png,image/svg+xml,image/x-icon,.ico"
        custom={!!c.favicon} busy={busy === 'favicon'}
        preview={c.favicon
          ? <img src={`/api/theme/public/favicon?v=${c.favicon_v || 0}`} alt="favicon" style={{ height: 48, width: 48, objectFit: 'contain' }} />
          : <img src="/favicon.svg" alt="favicon" style={{ height: 48, width: 48 }} />}
        onUpload={(f) => upload('favicon', f)} onReset={() => reset('favicon')}
      />
    </div>
  );
}

function AssetCard({ title, desc, accept, custom, busy, preview, onUpload, onReset }: {
  title: string; desc: string; accept: string; custom: boolean; busy: boolean;
  preview: React.ReactNode; onUpload: (f: File) => void; onReset: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="card p-4 space-y-3">
      <div><h2 className="font-semibold">{title}</h2><p className="text-textSecondary text-sm">{desc}</p></div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="rounded border border-border bg-elevated p-3 flex items-center justify-center" style={{ minWidth: 130, minHeight: 70 }}>{preview}</div>
        <div className="flex flex-col gap-2">
          <input ref={ref} type="file" accept={accept} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
          <button className="btn-primary inline-flex items-center gap-2" disabled={busy} onClick={() => ref.current?.click()}>
            <Upload size={14} /> {busy ? 'Uploading…' : (custom ? 'Replace' : 'Upload')}
          </button>
          {custom && (
            <button className="btn inline-flex items-center gap-2 text-danger" disabled={busy} onClick={onReset}>
              <Trash2 size={14} /> Reset to default
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const THEME_OPTIONS = [
  { id: 'default', name: 'Default', desc: 'Standard Stratus dark blue.',
    bg: '#1e2025', card: '#262830', primary: '#1e90e6', accent: '#22c55e', text: '#e8eaed' },
  { id: 'jukebox', name: 'Jukebox', desc: 'Dark glassmorphism — purple→violet + teal.',
    bg: '#0a0a12', card: 'rgba(255,255,255,0.07)', primary: 'linear-gradient(135deg,#7c5cff,#b06bff)', accent: '#2dd4bf', text: '#f3f3fb' },
  { id: 'futurio', name: 'Futurio', desc: 'Futuristic & glossy — electric cyan/blue, depth.',
    bg: '#070b16', card: 'linear-gradient(180deg,rgba(56,189,248,0.12),rgba(16,22,38,0.85))', primary: 'linear-gradient(135deg,#38bdf8,#2563eb)', accent: '#22d3ee', text: '#e2efff' },
  { id: 'beacon', name: 'Beacon', desc: 'Near-black living glass — single cyan accent.',
    bg: '#060608', card: 'rgba(9,11,15,0.62)', primary: 'linear-gradient(90deg,rgba(56,189,248,0.35),rgba(9,11,15,0.65))', accent: '#38bdf8', text: '#e6edf3' },
];

function ThemeTile({ t, active, busy, onPick }: { t: any; active: boolean; busy: boolean; onPick: () => void }) {
  return (
    <button onClick={onPick} disabled={busy}
      className={`text-left rounded-lg p-3 border transition ${active ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary'}`}
      style={{ background: t.bg }}>
      <div className="rounded-md p-2 mb-2" style={{ background: t.card, border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <span style={{ width: 10, height: 10, borderRadius: 999, background: t.accent, display: 'inline-block' }} />
          <span style={{ color: t.text, fontSize: 12, fontWeight: 600 }}>{t.name}</span>
        </div>
        <div style={{ height: 18, borderRadius: 6, background: t.primary }} />
      </div>
      <div style={{ color: t.text }} className="text-sm font-medium">{t.name}{active ? '  ✓' : ''}</div>
      <p style={{ color: t.text, opacity: 0.7 }} className="text-xs mt-0.5">{t.desc}</p>
    </button>
  );
}
